import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useForm } from "react-hook-form";
import axios from "axios";
import SweetAlertService from "../../utils/sweetalert";
import { Turnstile } from "@marsidev/react-turnstile";

interface ReportModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function ReportModal({ isOpen, onClose }: ReportModalProps) {
    const [turnstileToken, setTurnstileToken] = useState<string | null>(null); 
    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        reset,
        clearErrors,
        watch,
    } = useForm();

    const watchedFields = watch();

    const onSubmit = async (data: any) => {
        if (!turnstileToken) {
            SweetAlertService.chapchaAlert();
            return;
        }

        try {
            const formData = new FormData();
            if (data.img_report && data.img_report.length > 0) {
                formData.append("img_report", data.img_report[0]);
            }
            formData.append("name", data.name);
            formData.append("report_type", data.report_type);
            formData.append("description", data.description);
            formData.append("email", data.email);
            formData.append("phone", data.phone);
            formData.append("g-recaptcha-response", turnstileToken); 

            const response = await axios.post(`${process.env.REACT_APP_API_ENDPOINT}/report/create`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            const responseData = response.data;
            if (responseData.status === 'success') {
                SweetAlertService.successAlert();
                reset();
                clearErrors();
                setTurnstileToken(null);
                onClose();
            }
        } catch (error: any) {
            console.log(error);
            if (error.response && error.response.data) {
                const responseData = error.response.data;
                if (
                    responseData.data?.img_report?.includes(
                        "Image max size is 2048"
                    )
                ) {
                    SweetAlertService.maxFileSize();
                } else if (responseData.data?.img_report?.includes("Image must be jpeg, png, jpg, or webp")) {
                    SweetAlertService.mimesAlert();
                } else if (responseData.data?.email?.includes("The email field must be a valid email address.")) {
                    SweetAlertService.emailValidationAlert();
                } else if (
                    error.response.status === 422 &&
                    (responseData.message?.includes("Verifikasi") || responseData.error_codes)
                ) {
                    SweetAlertService.chapchaAlert();
                } else if (error.response.status === 422) {
                    SweetAlertService.warningAlert();
                } else {
                    SweetAlertService.errorAlert();
                }
            } else {
                SweetAlertService.errorAlert();
            }
        }
    };

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
            reset();
            clearErrors();
            setTurnstileToken(null);
        }

        return () => {
            document.body.style.overflow = "auto";
        };
    }, [isOpen]);

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50"
                >
                    <motion.div
                        initial={{ scale: 0.5, opacity: 0, y: -100 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.5, opacity: 0, y: 100 }}
                        transition={{
                            type: "spring",
                            stiffness: 300,
                            damping: 25,
                            duration: 0.3,
                        }}
                        className="bg-white p-6 rounded-lg shadow-lg w-96 max-h-[90vh] overflow-y-auto"
                    >
                        <h2 className="text-xl font-bold mb-4">
                            Laporkan Masalah atau Saran
                        </h2>
                        <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-2 gap-4">
                            <div className="col-span-1">
                                <label
                                    htmlFor="name"
                                    className="block text-sm font-medium mb-2"
                                >
                                    Nama anda <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    {...register("name", {
                                        required: "Nama wajib diisi",
                                    })}
                                    className={`mt-1 p-2 w-full border rounded-md transition-colors duration-300 focus:ring-0 ${errors.name
                                        ? "border-red-500 focus:border-red-500"
                                        : watchedFields.name
                                            ? "border-green-400"
                                            : "border-gray-300"
                                        }`}
                                    placeholder="Masukkan nama Anda"
                                />
                                {errors.name && (
                                    <p className="text-red-500 text-sm mt-1">
                                        {errors.name.message && typeof errors.name.message === 'string' && errors.name.message}
                                    </p>
                                )}
                            </div>
                            <div className="col-span-1">
                                <label
                                    htmlFor="email"
                                    className="block text-sm font-medium mb-2"
                                >
                                    Email anda <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="email"
                                    {...register("email", {
                                        required: "Email wajib diisi",
                                    })}
                                    className={`mt-1 p-2 w-full border rounded-md transition-colors duration-300 focus:ring-0 ${errors.email
                                        ? "border-red-500 focus:border-red-500"
                                        : watchedFields.email
                                            ? "border-green-400"
                                            : "border-gray-300"
                                        }`}
                                    placeholder="Masukkan email Anda"
                                />
                                {errors.email && (
                                    <p className="text-red-500 text-sm mt-1">
                                        {errors.email.message && typeof errors.email.message === 'string' && errors.email.message}
                                    </p>
                                )}
                            </div>
                            <div className="col-span-1">
                                <label
                                    htmlFor="phone"
                                    className="block text-sm font-medium mb-2"
                                >
                                    Nomor Handphone <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="phone"
                                    {...register("phone", {
                                        required: "Nomor Handphone wajib diisi",
                                        pattern: {
                                            value: /^[0-9]*$/,
                                            message: "Nomor Handphone harus berupa angka",
                                        },
                                    })}
                                    onKeyPress={(e) => {
                                        if (!/[0-9]/.test(e.key)) {
                                            e.preventDefault();
                                        }
                                    }}
                                    className={`mt-1 p-2 w-full border rounded-md transition-colors duration-300 focus:ring-0 ${errors.phone
                                        ? "border-red-500 focus:border-red-500"
                                        : watchedFields.phone
                                            ? "border-green-400"
                                            : "border-gray-300"
                                        }`}
                                    placeholder="Masukkan nomor handphone Anda"
                                />
                                {errors.phone && (
                                    <p className="text-red-500 text-sm mt-1">
                                        {errors.phone.message && typeof errors.phone.message === 'string' && errors.phone.message}
                                    </p>
                                )}
                            </div>
                            <div className="col-span-1">
                                <label
                                    htmlFor="report_type"
                                    className="block text-sm font-medium mb-2"
                                >
                                    Jenis Laporan <span className="text-red-500">*</span>
                                </label>
                                <select
                                    id="report_type"
                                    {...register("report_type", {
                                        required: "Jenis laporan wajib diisi",
                                    })}
                                    defaultValue=""
                                    className={`mt-1 p-2 w-full border rounded-md transition-colors duration-300 focus:ring-0 ${errors.report_type
                                        ? "border-red-500 focus:border-red-500"
                                        : watchedFields.report_type
                                            ? "border-green-400"
                                            : "border-gray-300"
                                        }`}
                                >
                                    <option value="" disabled hidden>
                                        Choose here
                                    </option>
                                    <option value="bug">Laporan Bug</option>
                                    <option value="saran">Saran</option>
                                    <option value="layanan">Pelayanan</option>
                                    <option value="lainnya">Lainnya</option>
                                </select>
                            </div>
                            <div className="col-span-2">
                                <label
                                    htmlFor="img_report"
                                    className="block text-sm font-medium mb-2"
                                >
                                    Gambar (opsional)
                                </label>
                                <input
                                    type="file"
                                    id="img_report"
                                    {...register("img_report")}
                                    className={`mt-1 p-2 w-full border rounded-md transition-colors duration-300 focus:ring-0 ${watchedFields.img_report ? "border-green-400" : "border-gray-300"
                                        }`}
                                    placeholder="Masukkan gambar Anda"
                                />
                            </div>
                            <div className="col-span-2">
                                <label
                                    htmlFor="description"
                                    className="block text-sm font-medium mb-2"
                                >
                                    Pesan <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    id="description"
                                    {...register("description", {
                                        required: "Pesan wajib diisi",
                                    })}
                                    rows={4}
                                    className={`w-full border rounded-md p-2 transition-colors duration-300 focus:ring-0 ${errors.description
                                        ? "border-red-500 focus:border-red-500"
                                        : watchedFields.description
                                            ? "border-green-400"
                                            : "border-gray-300"
                                        }`}
                                    placeholder="Tulis laporan atau saran Anda"
                                ></textarea>
                                {errors.description && (
                                    <p className="text-red-500 text-sm mt-1">
                                        {errors.description.message && typeof errors.description.message === 'string' && errors.description.message}
                                    </p>
                                )}
                            </div>


                            <div className="col-span-2 flex justify-center my-1">
                                {isOpen && ( 
                                    <Turnstile
                                        siteKey="0x4AAAAAAELyvSMdGUHqlu0F"
                                        onSuccess={(token) => setTurnstileToken(token)}
                                        onExpire={() => setTurnstileToken(null)}
                                    />
                                )}
                            </div>

                            <motion.div
                                className="col-span-2 flex justify-end gap-2"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                            >
                                <button
                                    type="button"
                                    className="bg-gray-300 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors focus:ring-0"
                                    onClick={onClose}
                                    disabled={isSubmitting}
                                >
                                    Batal
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="bg-bprYellow text-bprDarkBlue px-4 py-2 rounded-md hover:bg-yellow-400 transition-colors focus:ring-0 disabled:opacity-50"
                                >
                                    {isSubmitting ? "Mengirim..." : "Kirim"}
                                </button>
                            </motion.div>
                        </form>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}